import { useEffect } from 'react';
import { create } from 'zustand';
import type { Shortcut } from './use-keyboard-shortcuts';

/**
 * What the screen that is currently open contributes to the one shortcuts dialog.
 *
 * Every screen used to be free to bind its own keys and open its own dialog, and the
 * result was several dialogs answering the same key, each hiding the others from assistive
 * technology. One place decides now: the shell owns the key that opens the dialog, and a
 * screen only says which shortcuts of its own belong in it.
 */
interface ScreenShortcuts {
  shortcuts: Shortcut[];
  /** Names the screen's set inside the dialog, in the reader's language. */
  heading?: string;
}

interface ScreenShortcutsState {
  screen: ScreenShortcuts | null;
  setScreenShortcuts: (value: ScreenShortcuts) => void;
  clearScreenShortcuts: () => void;
  /** True while the shell shows the dialog, so a screen can hold its own keys. */
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

export const useScreenShortcutsStore = create<ScreenShortcutsState>()((set) => ({
  screen: null,
  setScreenShortcuts: (value) => set({ screen: value }),
  clearScreenShortcuts: () => set({ screen: null }),
  dialogOpen: false,
  setDialogOpen: (open) => set({ dialogOpen: open }),
}));

/**
 * Publishes a screen's shortcuts for as long as it is mounted. Only one screen is open at
 * a time, so the last to mount is the one the dialog shows.
 */
export function useScreenShortcuts(shortcuts: Shortcut[], heading?: string) {
  const setScreenShortcuts = useScreenShortcutsStore((state) => state.setScreenShortcuts);
  const clearScreenShortcuts = useScreenShortcutsStore((state) => state.clearScreenShortcuts);

  // The descriptions are the only part a reader sees, so a change of language has to reach
  // the dialog; the keys themselves are stable.
  const signature = shortcuts.map((s) => `${s.id}:${s.description}`).join('|');

  useEffect(() => {
    setScreenShortcuts({ shortcuts, heading });
    return () => clearScreenShortcuts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, heading, setScreenShortcuts, clearScreenShortcuts]);
}
